import type { SvgIconComponent } from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ArticleIcon from "@mui/icons-material/Article";
import BalanceIcon from "@mui/icons-material/Balance";
import CampaignIcon from "@mui/icons-material/Campaign";
import DescriptionIcon from "@mui/icons-material/Description";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ForumIcon from "@mui/icons-material/Forum";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PolicyIcon from "@mui/icons-material/Policy";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IEntityReponse } from "../../model/base/response/entity-response";
import { ouvUrl, sicUrl } from "../../utils/environment";

/** Rota interna do portal (ex.: "/licitacoes") ou URL absoluta de outro sistema (abre em nova aba). */
export interface IInstitutionalBannerAction {
  label: string;
  url: string;
}

export interface IInstitutionalBannerHighlight {
  label: string;
  icon: SvgIconComponent;
}

export interface IInstitutionalBanner {
  key: string;
  /** Rótulo curto exibido na navegação do carrossel. */
  tab: string;
  eyebrow: string;
  icon: SvgIconComponent;
  title: string;
  description: string;
  primaryAction: IInstitutionalBannerAction;
  secondaryAction?: IInstitutionalBannerAction;
  panelTitle: string;
  highlights: IInstitutionalBannerHighlight[];
  /** Base legal ou selo exibido no painel. */
  legalNote: string;
}

// entityType 2 = Câmara Municipal (mesma regra de utils/environment)
export const isChamber = (entity?: IEntityReponse): boolean => entity?.entityType === 2;

const cityHallBanners = (entity?: IEntityReponse): IInstitutionalBanner[] => [
  {
    key: "prefeitura",
    tab: "A Prefeitura",
    eyebrow: "Prefeitura Municipal",
    icon: AccountBalanceIcon,
    title: "Serviços, informações e transparência em um só lugar",
    description:
      "Canal oficial da Prefeitura para aproximar a gestão pública do cidadão: conheça os órgãos municipais, acompanhe as ações do governo e saiba como acessar cada serviço.",
    primaryAction: { label: "Ver Carta de Serviços", url: "/carta-servicos" },
    secondaryAction: { label: "Estrutura administrativa", url: "/estrutura-administrativa" },
    panelTitle: "Portal oficial",
    highlights: [
      { label: "Secretarias e órgãos municipais", icon: ApartmentIcon },
      { label: "Carta de Serviços ao Cidadão", icon: MenuBookIcon },
      { label: "Notícias e comunicados", icon: CampaignIcon },
      { label: "Projetos governamentais", icon: TaskAltIcon },
    ],
    legalNote: "Canal oficial do Município",
  },
  {
    key: "transparencia",
    tab: "Transparência",
    eyebrow: "Transparência pública",
    icon: ManageSearchIcon,
    title: "Cada real público à vista do cidadão",
    description:
      "Receitas, despesas, licitações, contratos e convênios reunidos para você acompanhar e fiscalizar como os recursos do Município são aplicados.",
    primaryAction: { label: "Acessar contas públicas", url: "/contas-publicas" },
    secondaryAction: { label: "Licitações", url: "/licitacoes" },
    panelTitle: "O que você encontra",
    highlights: [
      { label: "Receitas e despesas", icon: TrendingUpIcon },
      { label: "Licitações", icon: GavelIcon },
      { label: "Contratos e aquisições", icon: DescriptionIcon },
      { label: "Convênios e repasses", icon: HandshakeIcon },
    ],
    legalNote: "LC nº 131/2009 · Lei da Transparência",
  },
  {
    key: "diario-oficial",
    tab: "Diário Oficial",
    eyebrow: "Diário Oficial",
    icon: NewspaperIcon,
    title: "Atos oficiais publicados com transparência",
    description:
      "Consulte leis, decretos, portarias, editais e demais atos da administração municipal, organizados por data e categoria.",
    primaryAction: { label: "Consultar o Diário Oficial", url: "/diario-oficial" },
    secondaryAction: { label: "Publicações", url: "/publicacoes" },
    panelTitle: "Publicações oficiais",
    highlights: [
      { label: "Leis municipais", icon: BalanceIcon },
      { label: "Decretos", icon: ArticleIcon },
      { label: "Portarias", icon: DescriptionIcon },
      { label: "Editais e avisos", icon: EventNoteIcon },
    ],
    legalNote: "Princípio da publicidade · CF, art. 37",
  },
  {
    key: "ouvidoria",
    tab: "Ouvidoria",
    eyebrow: "Ouvidoria",
    icon: RecordVoiceOverIcon,
    title: "Sua voz ajuda a melhorar o serviço público",
    description:
      "Registre sugestões, elogios, solicitações, reclamações e denúncias. Cada manifestação recebe um protocolo para você acompanhar a resposta.",
    primaryAction: { label: "Registrar manifestação", url: ouvUrl(entity) },
    panelTitle: "Fale com a Prefeitura",
    highlights: [
      { label: "Sugestões", icon: LightbulbIcon },
      { label: "Elogios", icon: ThumbUpAltIcon },
      { label: "Reclamações", icon: ReportProblemIcon },
      { label: "Denúncias", icon: PolicyIcon },
    ],
    legalNote: "Lei nº 13.460/2017 · Direitos do usuário",
  },
  {
    key: "acesso-informacao",
    tab: "Acesso à Informação",
    eyebrow: "Acesso à Informação",
    icon: InfoIcon,
    title: "Informação pública é um direito seu",
    description:
      "Peça informações à Prefeitura pelo Serviço de Informação ao Cidadão (SIC), sem precisar justificar o motivo, e acompanhe o pedido até a resposta.",
    primaryAction: { label: "Fazer um pedido", url: sicUrl(entity) },
    panelTitle: "Como funciona",
    highlights: [
      { label: "Pedido sem justificativa", icon: HowToRegIcon },
      { label: "Resposta em até 20 dias", icon: ScheduleIcon },
      { label: "Direito a recurso", icon: BalanceIcon },
      { label: "Acompanhamento pelo protocolo", icon: TaskAltIcon },
    ],
    legalNote: "Lei nº 12.527/2011 · LAI",
  },
];

const chamberBanners = (entity?: IEntityReponse): IInstitutionalBanner[] => [
  {
    key: "camara",
    tab: "A Câmara",
    eyebrow: "Câmara Municipal",
    icon: AccountBalanceIcon,
    title: "O Poder Legislativo a serviço da população",
    description:
      "A Câmara Municipal representa os cidadãos, elabora as leis do Município e fiscaliza a aplicação dos recursos públicos. Acompanhe de perto o trabalho do Legislativo.",
    primaryAction: { label: "Conhecer a estrutura", url: "/estrutura-administrativa" },
    secondaryAction: { label: "Notícias da Câmara", url: "/noticias" },
    panelTitle: "Papel do Legislativo",
    highlights: [
      { label: "Legislar", icon: GavelIcon },
      { label: "Fiscalizar", icon: VisibilityIcon },
      { label: "Representar", icon: GroupsIcon },
      { label: "Debater", icon: ForumIcon },
    ],
    legalNote: "Poder Legislativo Municipal",
  },
  {
    key: "transparencia",
    tab: "Transparência",
    eyebrow: "Transparência legislativa",
    icon: ManageSearchIcon,
    title: "Transparência também no Legislativo",
    description:
      "Acompanhe as despesas, licitações, contratos e a prestação de contas da Câmara Municipal, com informações abertas a qualquer cidadão.",
    primaryAction: { label: "Acessar contas públicas", url: "/contas-publicas" },
    secondaryAction: { label: "Licitações", url: "/licitacoes" },
    panelTitle: "O que você encontra",
    highlights: [
      { label: "Despesas e repasses", icon: TrendingUpIcon },
      { label: "Licitações", icon: GavelIcon },
      { label: "Contratos e aquisições", icon: DescriptionIcon },
      { label: "Prestação de contas", icon: FactCheckIcon },
    ],
    legalNote: "LC nº 131/2009 · Lei da Transparência",
  },
  {
    key: "atos-legislativos",
    tab: "Atos legislativos",
    eyebrow: "Atos legislativos",
    icon: BalanceIcon,
    title: "Leis, resoluções e atos da Câmara ao seu alcance",
    description:
      "Consulte as publicações oficiais do Legislativo: leis, resoluções, decretos legislativos, portarias e atos da Mesa Diretora.",
    primaryAction: { label: "Consultar publicações", url: "/diario-oficial" },
    secondaryAction: { label: "Publicações", url: "/publicacoes" },
    panelTitle: "Publicações oficiais",
    highlights: [
      { label: "Leis", icon: BalanceIcon },
      { label: "Resoluções", icon: ArticleIcon },
      { label: "Decretos legislativos", icon: DescriptionIcon },
      { label: "Atos da Mesa Diretora", icon: EventNoteIcon },
    ],
    legalNote: "Princípio da publicidade · CF, art. 37",
  },
  {
    key: "ouvidoria",
    tab: "Ouvidoria",
    eyebrow: "Ouvidoria do Legislativo",
    icon: RecordVoiceOverIcon,
    title: "Participe: a Câmara quer ouvir você",
    description:
      "Envie sugestões, elogios, solicitações, reclamações e denúncias à Ouvidoria da Câmara. Cada manifestação recebe um protocolo para acompanhamento.",
    primaryAction: { label: "Registrar manifestação", url: ouvUrl(entity) },
    panelTitle: "Fale com a Câmara",
    highlights: [
      { label: "Sugestões", icon: LightbulbIcon },
      { label: "Elogios", icon: ThumbUpAltIcon },
      { label: "Reclamações", icon: ReportProblemIcon },
      { label: "Denúncias", icon: PolicyIcon },
    ],
    legalNote: "Lei nº 13.460/2017 · Direitos do usuário",
  },
  {
    key: "acesso-informacao",
    tab: "Acesso à Informação",
    eyebrow: "Acesso à Informação",
    icon: InfoIcon,
    title: "Informação pública é um direito seu",
    description:
      "Peça informações à Câmara pelo Serviço de Informação ao Cidadão (SIC), sem precisar justificar o motivo, e acompanhe o pedido até a resposta.",
    primaryAction: { label: "Fazer um pedido", url: sicUrl(entity) },
    panelTitle: "Como funciona",
    highlights: [
      { label: "Pedido sem justificativa", icon: HowToRegIcon },
      { label: "Resposta em até 20 dias", icon: ScheduleIcon },
      { label: "Direito a recurso", icon: BalanceIcon },
      { label: "Acompanhamento pelo protocolo", icon: TaskAltIcon },
    ],
    legalNote: "Lei nº 12.527/2011 · LAI",
  },
];

/** Banners institucionais exibidos no topo da home quando a entidade não possui banners cadastrados. */
export const getInstitutionalBanners = (entity?: IEntityReponse): IInstitutionalBanner[] =>
  isChamber(entity) ? chamberBanners(entity) : cityHallBanners(entity);
